// src/oura/fixtures/nyonya-question-sets.ts
//
// fixture-only: Nyonya Table's question sets, so the preview shows Question
// history. A live business reads the same shapes off its own scans
// (useScanQuestions); here the earlier scans' questions are written out as
// edits of today's 50.
//
// Two stories, picked by the URL (fixtures.ts):
//  - the default: v1 on 7 Jul, v2 from 18 Aug (the re-author the score
//    history already records), still v2 at the latest scan. The page reads as
//    any later scan of a set does: no banner, no New chips.
//  - `?sample=question-change`: the same business the day its questions
//    changed again. v2 runs 18 Aug and 2 Sep, and the latest scan is the first
//    on v3, so the banner, the v3 chips and the split all show.
//  - `?sample=same-day`: the change made the way a re-baseline should be,
//    the old set asked once more on the morning of 16 Sep and the new one
//    that afternoon, so two versions share a day and the split is exact.
//
// Each earlier scan's verdicts are fitted so its named count is exactly the
// one the score history records for it: the chart and the diff never disagree.

import type { AeoQuery, AeoScoreHistoryEntry } from "@service/aeo";
import { buildVisModel, type VisibilityInputs } from "../visibility/fromLive";
import { questionKey, type ScanQuestions } from "../visibility/questionSets";

/** "three-sets": the question-change story with one scan per set, so the line shows three dots, one per set. */
export type QuestionSetStory = "default" | "question-change" | "same-day" | "three-sets";

/** Most specific first: "Jalan Bukit Bintang" is Bukit Bintang, and Kuala Lumpur is the fallback. */
const AREAS = [
    "Pavilion KL", "Jalan Alor", "Lot 10", "Coronation Plaza", "Holland Village", "Clementi", "Sixth Avenue",
    "Botanic Gardens", "Dunearn Road", "King Albert Park", "Hillview", "Newton", "Bukit Bintang", "Kuala Lumpur",
];
const DISHES: [RegExp, string][] = [
    [/ayam buah keluak/i, "Ayam buah keluak"], [/laksa/i, "Laksa"], [/kueh/i, "Kueh"], [/chap chye/i, "Chap chye"],
    [/catering/i, "Catering"], [/set lunch/i, "Set lunch"], [/shophouse/i, "Heritage shophouse"],
    [/family recipe/i, "Family recipe"], [/family friendly/i, "Family friendly"], [/tourists/i, "Tourists"],
    [/halal/i, "Halal"], [/beginners/i, "Beginners"], [/cuisine|nyonya food/i, "Peranakan cuisine"],
];

/** What a slot was filled from: the area for a location question, the dish or need for a specialty one. */
function fillOf(q: AeoQuery): string | undefined {
    if (q.check === "location") return AREAS.find((a) => q.text.toLowerCase().includes(a.toLowerCase()));
    if (q.check === "specialty") return DISHES.find(([re]) => re.test(q.text))?.[1];
    return undefined;
}

/** A replacement in the same slot. `fill: undefined` keeps the slot's fill (a rewording). */
type Edit = { text: string; fill?: string | null };
const edit = (qs: AeoQuery[], edits: Record<string, Edit>): AeoQuery[] =>
    qs.map((q) => {
        const e = edits[q.text];
        if (!e) return q;
        return { ...q, text: e.text, fill: e.fill === undefined ? q.fill : e.fill ?? undefined };
    });

/** v2 back to v1: three rewordings in the same slots, and six swaps. */
const V1_FROM_V2: Record<string, Edit> = {
    "best nyonya laksa Kuala Lumpur": { text: "best laksa restaurant Kuala Lumpur" },
    "nyonya kueh platter Kuala Lumpur": { text: "where to buy nyonya kueh Kuala Lumpur" },
    "halal Peranakan restaurant Kuala Lumpur": { text: "halal nyonya food Kuala Lumpur" },
    "Peranakan food Holland Village": { text: "Peranakan restaurant Bangsar South", fill: "Bangsar South" },
    "Peranakan restaurant Clementi": { text: "nyonya food Cheras", fill: "Cheras" },
    "nyonya laksa Sixth Avenue": { text: "Peranakan restaurant Ampang", fill: "Ampang" },
    "Peranakan restaurant near Botanic Gardens": { text: "Peranakan restaurant KLCC", fill: "KLCC" },
    "Baba's Kitchen vs Nyonya Table": { text: "top Peranakan restaurant Kuala Lumpur", fill: null },
    "Heritage Rasa alternatives Bukit Bintang": { text: "Peranakan restaurant reviews Kuala Lumpur", fill: null },
};
/** v3 back to v2, in the question-change story: four areas, two services and two intent slots. */
const V2_FROM_V3: Record<string, Edit> = {
    "Peranakan restaurant Pavilion KL": { text: "Peranakan restaurant Bangsar", fill: "Bangsar" },
    "nyonya restaurant Jalan Alor": { text: "nyonya food Mid Valley", fill: "Mid Valley" },
    "Peranakan restaurant Lot 10": { text: "Peranakan restaurant Petaling Jaya", fill: "Petaling Jaya" },
    "Peranakan restaurant Coronation Plaza": { text: "Peranakan restaurant Mont Kiara", fill: "Mont Kiara" },
    "Peranakan set lunch Kuala Lumpur": { text: "Peranakan buffet Kuala Lumpur", fill: "Buffet" },
    "Peranakan catering Kuala Lumpur": { text: "nyonya cooking class Kuala Lumpur", fill: "Cooking class" },
    "date night restaurant Bukit Bintang": { text: "romantic Peranakan dinner Kuala Lumpur", fill: null },
    "restaurant for large group Bukit Bintang": { text: "Peranakan restaurant private room Kuala Lumpur", fill: null },
};
/** How the questions that are not asked today read on their own scans. */
const OLD_NAMED = new Set([
    "best laksa restaurant Kuala Lumpur", "Peranakan restaurant KLCC", "top Peranakan restaurant Kuala Lumpur",
    "romantic Peranakan dinner Kuala Lumpur",
].map(questionKey));

/**
 * A scan's verdicts with exactly `target` named: today's verdict where the
 * question is asked today, the old one where it is not, then the fewest flips
 * to land on the recorded count.
 */
function fitted(scan: { id: string; at: string; target: number }, queries: AeoQuery[], now: Record<string, boolean>): ScanQuestions {
    const keys = queries.map((q) => questionKey(q.text));
    const named: Record<string, boolean> = Object.fromEntries(keys.map((k) => [k, now[k] ?? OLD_NAMED.has(k)]));
    let count = keys.filter((k) => named[k]).length;
    for (let i = keys.length - 1; i >= 0 && count > scan.target; i -= 1) {
        if (named[keys[i]]) { named[keys[i]] = false; count -= 1; }
    }
    for (let i = 0; i < keys.length && count < scan.target; i += 1) {
        if (!named[keys[i]]) { named[keys[i]] = true; count += 1; }
    }
    return { assessmentId: scan.id, capturedAt: scan.at, queries, named };
}

const JUL = { id: "nyonya-table-2026-07-07", at: "2026-07-07T08:00:00Z", target: 19 };
const AUG = { id: "nyonya-table-2026-08-18", at: "2026-08-18T08:00:00Z", target: 21 };
const SEP = { id: "nyonya-table-2026-09-02", at: "2026-09-02T08:00:00Z", target: 22 };
/** The old set asked again the morning the questions changed (same-day story). */
const BRIDGE = { id: "nyonya-table-2026-09-16-bridge", at: "2026-09-16T02:30:00Z", target: 22 };

/** The fixture with its question sets filled in, for one of the two stories. */
export function withQuestionSets(base: VisibilityInputs, story: QuestionSetStory): VisibilityInputs {
    const today = base.queries.map((q) => ({ ...q, fill: fillOf(q) }));
    const model = buildVisModel(base, null);
    const now: Record<string, boolean> = Object.fromEntries(model.rows.map((r) => [r.key, r.named]));

    if (story === "default") {
        const v1 = edit(today, V1_FROM_V2);
        return {
            ...base,
            queries: today,
            questionSets: {
                [JUL.id]: fitted(JUL, v1, now),
                [AUG.id]: fitted(AUG, today, now),
            },
        };
    }

    const v2 = edit(today, V2_FROM_V3);
    const v1 = edit(v2, V1_FROM_V2);
    const extra = story === "same-day" ? BRIDGE : story === "three-sets" ? null : SEP;
    const [latest, ...rest] = base.history;
    const changedAt = latest.captured_at;
    const history: AeoScoreHistoryEntry[] = [
        {
            ...latest,
            visibility: latest.visibility
                ? { ...latest.visibility, questionSetId: "set-v4.3-nyonya", trendBreak: { at: changedAt, reason: "the question set changed here" } }
                : null,
        },
        ...(extra ? [{
            assessment_id: extra.id, captured_at: extra.at, aeo_score: 37, seo_score: 62, gated: false, status: "complete",
            cost: { actual_usd: 0.6, estimated_usd: null },
            visibility: {
                questionsNamed: extra.target, questionsOf: 50, recipe: "visibility-set-v4.2", queriesFrozenAt: AUG.at,
                questionSetId: "set-v4.2-nyonya", trendBreak: null,
                engines: { chatgpt: { named: 17, answered: 50, instrument: "chatgpt_app" }, gemini: { named: 14, answered: 50 }, googleAi: null },
            },
        } satisfies AeoScoreHistoryEntry] : []),
        ...rest,
    ];
    return {
        ...base,
        queries: today,
        history,
        trendBreak: { at: changedAt, reason: "the question set changed here" },
        // The scan before asked other questions, so there is no comparable
        // scan for the Competitors tab, as on a live business.
        prior: null,
        questionSets: {
            [JUL.id]: fitted(JUL, v1, now),
            [AUG.id]: fitted(AUG, v2, now),
            ...(extra ? { [extra.id]: fitted(extra, v2, now) } : {}),
        },
    };
}
